import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Sparkles, BookMarked, ChevronRight, RotateCcw,
  X, Loader2, PanelRightClose, PanelRightOpen,
} from "lucide-react";
import { EditionShell } from "../components/dnh/feed/EditionShell";
import { StoryGrid } from "../components/dnh/StoryGrid";
import { useAuth } from "../components/dnh/AuthContext";
import { useBookmarks } from "../components/dnh/BookmarkContext";
import { timeAgo } from "../components/dnh/home/homeUtils";
import { toast } from "sonner";
import { apiFetch } from "../utils/api";

const BASE = process.env.REACT_APP_BACKEND_URL;

/* ──────────────────────────────────────────────────────────
   Guest sign-in prompt
   ────────────────────────────────────────────────────────── */
const GuestPrompt = () => (
  <div
    data-testid="for-you-guest-prompt"
    className="py-20 flex flex-col items-center text-center max-w-sm mx-auto"
  >
    <div className="w-16 h-16 rounded-full bg-[var(--surface-2)] border border-[var(--hairline)] flex items-center justify-center mb-6">
      <Sparkles size={26} className="text-[var(--crimson)]" />
    </div>
    <h2 className="font-serif text-2xl text-[var(--ink)] mb-3">
      Your personal news feed
    </h2>
    <p className="text-[14px] text-[var(--muted)] leading-relaxed mb-8">
      Sign in to see stories chosen just for you. Every like, save and
      dismissal shapes a feed that matches what you care about.
    </p>
    <div className="flex gap-3 flex-wrap justify-center">
      <Link
        to="/signin"
        data-testid="for-you-signin-btn"
        className="px-6 py-2.5 rounded-full bg-[var(--ink)] text-[var(--paper)] text-[13px] font-medium hover:bg-[var(--crimson)] transition-colors"
      >
        Sign in
      </Link>
      <Link
        to="/signup"
        data-testid="for-you-signup-btn"
        className="px-6 py-2.5 rounded-full border border-[var(--hairline-strong)] text-[var(--ink)] text-[13px] font-medium hover:border-[var(--crimson)] hover:text-[var(--crimson)] transition-colors"
      >
        Create account
      </Link>
    </div>
  </div>
);

/* ──────────────────────────────────────────────────────────
   Compact Reading List item (sidebar)
   ────────────────────────────────────────────────────────── */
const ReadingListItem = ({ story, onRemove }) => {
  const navigate = useNavigate();
  const sid = story.id || story.story_id;
  return (
    <div
      data-testid={`reading-list-item-${sid}`}
      className="py-3 border-b border-[var(--hairline)] last:border-0 group flex items-start gap-2"
    >
      <button
        onClick={() => navigate(`/briefs/${sid}`)}
        className="flex-1 min-w-0 text-left"
      >
        {story.image_url && (
          <div
            className="w-full h-14 rounded-md bg-cover bg-center mb-2 overflow-hidden"
            style={{ backgroundImage: `url(${story.image_url})` }}
          />
        )}
        <p className="text-[12.5px] font-medium text-[var(--ink)] line-clamp-2 group-hover:text-[var(--crimson)] transition-colors leading-snug">
          {story.headline || story.title}
        </p>
        <div className="flex items-center gap-1.5 mt-1 min-w-0">
          <span className="text-[10.5px] text-[var(--muted)] truncate">
            {story.source || story.publisher}
          </span>
          <span className="text-[10px] text-[var(--muted)] shrink-0">·</span>
          <span className="text-[10.5px] text-[var(--muted)] shrink-0">
            {timeAgo(story.published_at || story.saved_at)}
          </span>
        </div>
      </button>
      <button
        onClick={() => onRemove(sid)}
        aria-label="Remove from Reading List"
        data-testid={`reading-list-remove-${sid}`}
        className="shrink-0 w-6 h-6 mt-0.5 rounded-full flex items-center justify-center text-[var(--muted)] hover:text-[var(--crimson)] hover:bg-[var(--surface-2)] transition-colors opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
      >
        <X size={11} strokeWidth={2.5} />
      </button>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────
   Reading List sidebar
   ────────────────────────────────────────────────────────── */
const ReadingListSidebar = ({ onCollapse }) => {
  const { user } = useAuth();
  const { bookmarkedIds, syncing } = useBookmarks();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [error, setError] = useState(false);
  const key = [...bookmarkedIds].sort().join(",");

  useEffect(() => {
    if (!user || syncing) return;
    let active = true;
    setLoading(true);
    setError(false);
    (async () => {
      try {
        const res = await fetch(
          `${BASE}/api/user/saved?q=${encodeURIComponent(query)}`,
          { credentials: "include" }
        );
        if (!res.ok) throw new Error();
        const data = await res.json();
        if (active) setStories(data.saved || []);
      } catch {
        if (active) setError(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [user, key, syncing, query]);

  const handleRemove = useCallback(async (storyId) => {
    try {
      await apiFetch(`/api/user/saved/${storyId}`, { method: "DELETE" });
      setStories(prev => prev.filter(s => (s.id || s.story_id) !== storyId));
      toast.success("Removed from Reading List");
    } catch {
      toast.error("Could not remove story");
    }
  }, []);

  return (
    <div className="flex flex-col h-full" data-testid="reading-list-sidebar">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 pb-3 border-b border-[var(--hairline)]">
        <div className="flex items-center gap-2">
          <BookMarked size={15} strokeWidth={1.75} className="text-[var(--ink-2)]" />
          <h2
            className="text-[13px] font-semibold text-[var(--ink)] uppercase tracking-[0.06em]"
            data-testid="reading-list-header"
          >
            Reading List
          </h2>
          {!loading && (
            <span className="text-[10px] bg-[var(--surface-2)] text-[var(--muted)] px-1.5 py-0.5 rounded-full border border-[var(--hairline)]">
              {stories.length}
            </span>
          )}
        </div>
        {/* Collapse button — desktop only */}
        <button
          onClick={onCollapse}
          aria-label="Collapse Reading List"
          data-testid="reading-list-collapse-btn"
          className="hidden lg:flex w-7 h-7 items-center justify-center rounded-md text-[var(--muted)] hover:text-[var(--ink)] hover:bg-[var(--surface-2)] transition-colors"
        >
          <PanelRightClose size={15} strokeWidth={1.75} />
        </button>
      </div>

      {/* Search */}
      <div className="mb-3">
        <input
          type="search"
          placeholder="Search saved stories…"
          value={query}
          onChange={e => setQuery(e.target.value)}
          data-testid="reading-list-search"
          className="w-full h-8 px-3 text-[12px] rounded-lg border border-[var(--hairline)] bg-[var(--surface-2)] text-[var(--ink)] placeholder:text-[var(--muted)] focus:outline-none focus:border-[var(--ink)] transition-colors"
        />
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto pr-0.5">
        {(syncing || loading) ? (
          <div className="flex justify-center py-10">
            <Loader2 size={18} className="animate-spin text-[var(--muted)]" />
          </div>
        ) : error ? (
          <p className="text-[12px] text-[var(--muted)] py-6 text-center">
            Could not load saved stories
          </p>
        ) : stories.length ? (
          stories.map(s => (
            <ReadingListItem
              key={s.id || s.story_id}
              story={s}
              onRemove={handleRemove}
            />
          ))
        ) : (
          <div className="py-10 text-center" data-testid="reading-list-empty">
            <p className="text-[12px] text-[var(--muted)] leading-relaxed">
              {query
                ? "No stories match this search"
                : "No saved stories yet — use the three-dots menu on any card to save one"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────
   Suggested feed (left column)
   ────────────────────────────────────────────────────────── */
const SuggestedFeed = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(`${BASE}/api/suggested?limit=30`, {
          credentials: "include",
        });
        if (!res.ok) throw new Error();
        const d = await res.json();
        if (active) setData(d);
      } catch {
        if (active) setData(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [retry]);

  const handleReset = async () => {
    setResetting(true);
    try {
      await apiFetch("/api/user/preferences/reset", { method: "POST" });
      toast.success("Preferences cleared — feed refreshed");
      setRetry(v => v + 1);
    } catch {
      toast.error("Could not reset preferences");
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 size={24} className="animate-spin text-[var(--muted)]" />
      </div>
    );
  }

  const stories = data?.stories || [];
  const hasPrefs = data?.has_preferences;
  const signalCount = data?.signal_count || 0;

  return (
    <div>
      {/* Preference transparency card */}
      <div
        data-testid="preference-transparency-card"
        className="flex items-center gap-3 mb-6 px-4 py-3 rounded-xl bg-[var(--surface-2)] border border-[var(--hairline)]"
      >
        <Sparkles size={14} className="text-[var(--crimson)] shrink-0" />
        <p className="flex-1 text-[12px] text-[var(--ink-2)] min-w-0 leading-snug">
          {hasPrefs
            ? `Personalised from ${signalCount} topic${signalCount !== 1 ? "s" : ""} and source${signalCount !== 1 ? "s" : ""} you interacted with`
            : "Like, save and dismiss stories — your personalised feed builds automatically"}
        </p>
        {hasPrefs && (
          <button
            onClick={handleReset}
            disabled={resetting}
            data-testid="reset-preferences-btn"
            title="Clear personalisation and start fresh"
            className="shrink-0 flex items-center gap-1 text-[11px] text-[var(--muted)] hover:text-[var(--crimson)] transition-colors disabled:opacity-50"
          >
            <RotateCcw
              size={11}
              strokeWidth={2}
              className={resetting ? "animate-spin" : ""}
            />
            Reset
          </button>
        )}
      </div>

      {/* Stories grid */}
      {stories.length ? (
        <StoryGrid
          stories={stories}
          testId="suggested-stories-grid"
          from="for-you"
        />
      ) : (
        <div
          data-testid="suggested-empty-state"
          className="py-20 flex flex-col items-center text-center max-w-xs mx-auto"
        >
          <Sparkles size={28} className="text-[var(--muted)] mb-4" />
          <h3 className="font-serif text-lg text-[var(--ink)] mb-2">
            No stories yet
          </h3>
          <p className="text-[13px] text-[var(--muted)] leading-relaxed">
            No recent stories match your interests. Start interacting with stories
            from Today or Search to fill this feed.
          </p>
          <Link
            to="/today"
            data-testid="suggested-today-link"
            className="inline-flex items-center gap-1.5 mt-6 text-[13px] text-[var(--ink-2)] hover:text-[var(--crimson)] transition-colors"
          >
            Browse today's stories <ChevronRight size={13} />
          </Link>
        </div>
      )}
    </div>
  );
};

/* ──────────────────────────────────────────────────────────
   Mobile tabs
   ────────────────────────────────────────────────────────── */
const TABS = [
  { id: "suggested",    label: "Suggested" },
  { id: "reading-list", label: "Reading List" },
];

const MobileTabs = ({ active, onChange }) => (
  <div className="flex lg:hidden border-b border-[var(--hairline)] mb-6 -mt-2">
    {TABS.map(tab => (
      <button
        key={tab.id}
        onClick={() => onChange(tab.id)}
        data-testid={`for-you-tab-${tab.id}`}
        className={`px-4 py-2.5 text-[13px] font-medium border-b-2 -mb-px transition-colors ${
          active === tab.id
            ? "border-[var(--crimson)] text-[var(--ink)]"
            : "border-transparent text-[var(--muted)] hover:text-[var(--ink-2)]"
        }`}
      >
        {tab.label}
      </button>
    ))}
  </div>
);

/* ──────────────────────────────────────────────────────────
   For You page — main export
   ────────────────────────────────────────────────────────── */
export default function ForYou() {
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState("suggested");

  return (
    <EditionShell
      title="For You"
      activeNav="For You"
      testId="for-you-page"
      eyebrow="Personalised"
    >
      {/* ── Guest state ── */}
      {!user ? (
        <GuestPrompt />
      ) : (
        <>
          {/* ── Mobile tab bar ── */}
          <MobileTabs active={activeTab} onChange={setActiveTab} />

          {/* ── Two-column layout ── */}
          <div className="flex gap-8 items-start">
            {/* Left: Suggested feed */}
            <div
              className={`flex-1 min-w-0 ${
                activeTab === "reading-list" ? "hidden lg:block" : ""
              }`}
            >
              <SuggestedFeed />
            </div>

            {/* Right: Reading List sidebar */}
            {sidebarOpen && (
              <div
                className={`
                  lg:w-72 xl:w-80 lg:shrink-0
                  lg:sticky lg:top-[130px] lg:max-h-[calc(100vh-150px)] lg:overflow-hidden lg:flex lg:flex-col
                  ${activeTab === "suggested" ? "hidden lg:flex" : "w-full flex flex-col"}
                `}
                data-testid="reading-list-sidebar-wrap"
              >
                <ReadingListSidebar onCollapse={() => setSidebarOpen(false)} />
              </div>
            )}

            {/* Collapsed sidebar: reopen button (desktop only) */}
            {!sidebarOpen && (
              <button
                onClick={() => setSidebarOpen(true)}
                data-testid="reading-list-expand-btn"
                title="Show Reading List"
                className="hidden lg:flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[var(--hairline)] text-[12px] text-[var(--muted)] hover:text-[var(--ink)] hover:border-[var(--hairline-strong)] transition-colors shrink-0 self-start"
              >
                <PanelRightOpen size={14} strokeWidth={1.75} />
                Reading List
              </button>
            )}
          </div>
        </>
      )}
    </EditionShell>
  );
}
